import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import chatService from '../services/chatService';

const ChatContext = createContext(null);

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children, currentUser }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadPerUser, setUnreadPerUser] = useState({});
  const [autoOpenEnabled, setAutoOpenEnabled] = useState(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Group chat state
  const [activeTab, setActiveTab] = useState('dms'); // 'dms' | 'groups'
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [unreadPerGroup, setUnreadPerGroup] = useState({});
  const [groupUnreadCount, setGroupUnreadCount] = useState(0);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showGroupSettings, setShowGroupSettings] = useState(false);

  // Track last seen message to avoid re-opening for same message
  const lastSeenMessageRef = useRef(null);

  // Load user preferences on mount
  useEffect(() => {
    if (currentUser?.id) {
      loadPreferences();
    }
  }, [currentUser?.id]);

  // Start unread polling when we have a user
  useEffect(() => {
    if (currentUser?.id && preferencesLoaded) {
      chatService.startUnreadPolling(currentUser.id, handleUnreadUpdate, 10000);

      return () => {
        chatService.stopUnreadPolling();
      };
    }
  }, [currentUser?.id, preferencesLoaded, autoOpenEnabled, isPanelOpen]);

  const loadPreferences = async () => {
    try {
      const result = await chatService.getPreferences(currentUser.id);
      if (result.success) {
        setAutoOpenEnabled(result.preferences.auto_open_enabled);
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    } finally {
      setPreferencesLoaded(true);
    }
  };

  // Load per-user unread counts
  const loadUnreadPerUser = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const result = await chatService.getUnreadCountsPerUser(currentUser.id);
      if (result.success) {
        setUnreadPerUser(result.unread_counts || {});
      }
    } catch (err) {
      console.error('Failed to load per-user unread counts:', err);
    }
  }, [currentUser?.id]);

  // Load per-group unread counts
  const loadUnreadPerGroup = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const result = await chatService.getUnreadCountsPerGroup(currentUser.id);
      if (result.success) {
        setUnreadPerGroup(result.unread_counts || {});
        // Calculate total group unread
        const total = Object.values(result.unread_counts || {}).reduce((sum, count) => sum + count, 0);
        setGroupUnreadCount(total);
      }
    } catch (err) {
      console.error('Failed to load per-group unread counts:', err);
    }
  }, [currentUser?.id]);

  // Load groups for user
  const loadGroups = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const result = await chatService.getGroups(currentUser.id);
      if (result.success) {
        setGroups(result.groups || []);
      }
      loadUnreadPerGroup();
    } catch (err) {
      console.error('Failed to load groups:', err);
    }
  }, [currentUser?.id, loadUnreadPerGroup]);

  const handleUnreadUpdate = useCallback(async (result) => {
    if (result.success) {
      setUnreadCount(result.unread_count);

      // Also fetch per-user counts when total changes
      loadUnreadPerUser();
      // Also fetch per-group counts
      loadUnreadPerGroup();

      // Auto-open logic: if enabled, panel is closed, and we have unread messages
      if (autoOpenEnabled && !isPanelOpen && result.unread_count > 0) {
        // Check for new message details to auto-select the sender
        try {
          const newMsgResult = await chatService.checkNewMessages(currentUser.id);
          if (newMsgResult.success && newMsgResult.has_new_message) {
            // Only auto-open if this is a new message we haven't seen
            const messageKey = `${newMsgResult.sender_id}_${newMsgResult.created_at}`;
            if (lastSeenMessageRef.current !== messageKey) {
              lastSeenMessageRef.current = messageKey;

              // Open panel and select the sender
              setIsPanelOpen(true);

              // Find sender in users list or create temp user object
              const senderUser = users.find(u => u.id === newMsgResult.sender_id) || {
                id: newMsgResult.sender_id,
                name: newMsgResult.sender_name
              };

              // Delay selecting user to allow panel to render
              setTimeout(() => {
                selectUser(senderUser);
              }, 100);
            }
          }
        } catch (err) {
          console.error('Failed to check new messages:', err);
        }
      }
    }
  }, [autoOpenEnabled, isPanelOpen, currentUser?.id, users, loadUnreadPerUser, loadUnreadPerGroup]);

  const toggleAutoOpen = useCallback(async () => {
    if (!currentUser?.id) return;

    const newValue = !autoOpenEnabled;
    setAutoOpenEnabled(newValue);

    try {
      await chatService.updatePreferences(currentUser.id, newValue);
    } catch (err) {
      // Revert on error
      setAutoOpenEnabled(!newValue);
      console.error('Failed to update auto-open preference:', err);
    }
  }, [currentUser?.id, autoOpenEnabled]);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    chatService.stopPolling();
    chatService.stopGroupPolling();
    setSelectedUser(null);
    setSelectedGroup(null);
  }, []);

  const togglePanel = useCallback(() => {
    if (isPanelOpen) {
      closePanel();
    } else {
      openPanel();
    }
  }, [isPanelOpen, openPanel, closePanel]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await chatService.getChatUsers(currentUser?.id);
      setUsers(data.users || []);
      // Also load per-user unread counts when loading users
      loadUnreadPerUser();
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, loadUnreadPerUser]);

  const selectUser = useCallback(async (user) => {
    setSelectedUser(user);
    setMessages([]);

    if (user && currentUser?.id) {
      // Start polling for messages between current user and selected user
      chatService.startPolling(currentUser.id, user.id, (data) => {
        setMessages(data.messages || []);
      });

      // Clear the unread count for this user locally (messages will be marked read on server)
      setUnreadPerUser(prev => {
        const updated = { ...prev };
        delete updated[user.id];
        return updated;
      });
    } else {
      chatService.stopPolling();
    }
  }, [currentUser]);

  const sendMessage = useCallback(async (message) => {
    if (!selectedUser || !message.trim() || !currentUser?.id) return;

    try {
      await chatService.sendMessage(
        currentUser.id,
        selectedUser.id,
        message
      );

      // Refresh messages immediately after sending
      const data = await chatService.getMessages(currentUser.id, selectedUser.id);
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Failed to send message:', err);
      throw err;
    }
  }, [selectedUser, currentUser]);

  // ===== GROUP FUNCTIONS =====

  const switchTab = useCallback((tab) => {
    setActiveTab(tab);
    // Clear selections when switching tabs
    if (tab === 'dms') {
      setSelectedGroup(null);
      chatService.stopGroupPolling();
    } else {
      setSelectedUser(null);
      chatService.stopPolling();
      loadGroups();
    }
  }, [loadGroups]);

  const selectGroup = useCallback(async (group) => {
    // Clear DM state
    setSelectedUser(null);
    chatService.stopPolling();

    // Set group state
    setSelectedGroup(group);
    setGroupMessages([]);
    setGroupMembers([]);

    if (group && currentUser?.id) {
      // Load group members
      try {
        const membersResult = await chatService.getGroupMembers(group.id);
        if (membersResult.success) {
          setGroupMembers(membersResult.members || []);
        }
      } catch (err) {
        console.error('Failed to load group members:', err);
      }

      // Start polling for group messages
      chatService.startGroupPolling(currentUser.id, group.id, (data) => {
        setGroupMessages(data.messages || []);
      });

      // Clear unread for this group locally
      setUnreadPerGroup(prev => {
        const updated = { ...prev };
        delete updated[group.id];
        return updated;
      });
    } else {
      chatService.stopGroupPolling();
    }
  }, [currentUser]);

  const sendGroupMessage = useCallback(async (message) => {
    if (!selectedGroup || !message.trim() || !currentUser?.id) return;

    try {
      await chatService.sendGroupMessage(
        selectedGroup.id,
        currentUser.id,
        message
      );

      // Refresh messages immediately after sending
      const data = await chatService.getGroupMessages(selectedGroup.id, currentUser.id);
      setGroupMessages(data.messages || []);
    } catch (err) {
      console.error('Failed to send group message:', err);
      throw err;
    }
  }, [selectedGroup, currentUser]);

  const createGroup = useCallback(async (name, memberIds) => {
    if (!currentUser?.id || !name.trim()) return;

    try {
      const result = await chatService.createGroup(name, currentUser.id, memberIds);
      if (result.success) {
        // Reload groups list
        await loadGroups();
        return result;
      }
    } catch (err) {
      console.error('Failed to create group:', err);
      throw err;
    }
  }, [currentUser, loadGroups]);

  const addGroupMember = useCallback(async (groupId, userId) => {
    try {
      const result = await chatService.addGroupMember(groupId, userId);
      if (result.success && selectedGroup?.id === groupId) {
        // Refresh members list
        const membersResult = await chatService.getGroupMembers(groupId);
        if (membersResult.success) {
          setGroupMembers(membersResult.members || []);
        }
      }
      return result;
    } catch (err) {
      console.error('Failed to add group member:', err);
      throw err;
    }
  }, [selectedGroup]);

  const removeGroupMember = useCallback(async (groupId, userId) => {
    try {
      const result = await chatService.removeGroupMember(groupId, userId);
      if (result.success && selectedGroup?.id === groupId) {
        // Refresh members list
        const membersResult = await chatService.getGroupMembers(groupId);
        if (membersResult.success) {
          setGroupMembers(membersResult.members || []);
        }
      }
      return result;
    } catch (err) {
      console.error('Failed to remove group member:', err);
      throw err;
    }
  }, [selectedGroup]);

  const deleteGroup = useCallback(async (groupId) => {
    try {
      const result = await chatService.deleteGroup(groupId);
      if (result.success) {
        // If we deleted the selected group, deselect it
        if (selectedGroup?.id === groupId) {
          setSelectedGroup(null);
          chatService.stopGroupPolling();
        }
        // Reload groups list
        await loadGroups();
      }
      return result;
    } catch (err) {
      console.error('Failed to delete group:', err);
      throw err;
    }
  }, [selectedGroup, loadGroups]);

  const openCreateGroupModal = useCallback(() => {
    setShowCreateGroupModal(true);
  }, []);

  const closeCreateGroupModal = useCallback(() => {
    setShowCreateGroupModal(false);
  }, []);

  const openGroupSettings = useCallback(() => {
    setShowGroupSettings(true);
  }, []);

  const closeGroupSettings = useCallback(() => {
    setShowGroupSettings(false);
  }, []);

  const value = {
    // DM state
    isPanelOpen,
    users,
    selectedUser,
    messages,
    loading,
    error,
    currentUser,
    unreadCount,
    unreadPerUser,
    autoOpenEnabled,
    // Group state
    activeTab,
    groups,
    selectedGroup,
    groupMessages,
    groupMembers,
    unreadPerGroup,
    groupUnreadCount,
    showCreateGroupModal,
    showGroupSettings,
    // DM actions
    openPanel,
    closePanel,
    togglePanel,
    loadUsers,
    selectUser,
    sendMessage,
    toggleAutoOpen,
    // Group actions
    switchTab,
    loadGroups,
    selectGroup,
    sendGroupMessage,
    createGroup,
    addGroupMember,
    removeGroupMember,
    deleteGroup,
    openCreateGroupModal,
    closeCreateGroupModal,
    openGroupSettings,
    closeGroupSettings,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
